import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, RefreshCw, X } from "lucide-react";
import { fmtIST } from "@/lib/time";

export const DEFAULT_STAGES = ["Applied", "Screened", "Task submitted", "Interview booked", "Interviewed", "Offer sent", "Offer accepted", "Rejected", "Talent pool"];
const NON_INPUT = ["heading", "rich_text", "video", "image", "link", "page_break", "calendar_booking"];
const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/;

function Linkified({ text }) {
  const parts = String(text).split(URL_RE);
  return parts.map((p, i) => (i % 2
    ? <a key={i} href={p.startsWith("http") ? p : `https://${p}`} target="_blank" rel="noreferrer" className="text-[#5B9BFF] underline break-all">{p}</a>
    : <span key={i}>{p}</span>));
}

function AnswerValue({ v }) {
  if (Array.isArray(v)) return <Linkified text={v.join(", ")} />;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v && typeof v === "object") return v.url ? <Linkified text={v.url} /> : (v.name || JSON.stringify(v));
  return <Linkified text={v} />;
}

function Answers({ form, answers }) {
  const a = answers || {};
  const has = (v) => v !== undefined && v !== null && v !== "";
  const blocks = (form?.blocks || []).filter((b) => !NON_INPUT.includes(b.kind) && has(a[b.block_id]));
  const known = new Set((form?.blocks || []).map((b) => b.block_id));
  const extra = Object.entries(a).filter(([k, v]) => !known.has(k) && has(v));
  const rows = [...blocks.map((b) => [b.block_id, b.label || b.kind, a[b.block_id]]), ...extra.map(([k, v]) => [k, k, v])];
  if (!rows.length) return <div className="text-sm text-[#71717A] mb-4">No answers recorded.</div>;
  return (
    <div className="space-y-3 mb-5" data-testid="candidate-answers">
      {rows.map(([k, label, v]) => (
        <div key={k}>
          <div className="text-xs text-[#71717A] mb-0.5">{label}</div>
          <div className="text-sm whitespace-pre-wrap break-words"><AnswerValue v={v} /></div>
        </div>
      ))}
    </div>
  );
}

function CommsHistory({ log }) {
  if (!log.length) return <div className="text-sm text-[#71717A] mb-4">No messages sent yet.</div>;
  const cls = (s) => (s === "sent" ? "tt-badge-pass" : s === "failed" ? "tt-badge-danger" : "tt-badge-warn");
  return (
    <div className="space-y-2 mb-5" data-testid="candidate-comms-history">
      {log.map((l) => (
        <div key={l.log_id} className="tt-card p-3 text-xs">
          <div className="flex justify-between mb-1"><span className="font-mono uppercase text-[#A1A1AA]">{l.channel}</span><span className={cls(l.status)}>{l.status}{l.status === "queued" && l.channel === "email" ? " · Google not connected" : ""}</span></div>
          <div className="text-[#A1A1AA] line-clamp-2">{l.body}</div>
          <div className="text-[#52525B] mt-1">{fmtIST(l.sent_at)}{l.opened_at ? ` · opened ${fmtIST(l.opened_at)}` : ""}</div>
        </div>
      ))}
    </div>
  );
}

export function CandidateDrawer({ sub, form, onClose, onChanged, onMessage }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const stages = form?.stages?.length ? form.stages : DEFAULT_STAGES;

  useEffect(() => {
    api.get("/comms/log", { params: { submission_id: sub.submission_id } }).then((r) => setLog(r.data || [])).catch(() => setLog([]));
  }, [sub.submission_id]);

  const patch = async (body, msg) => {
    setBusy(true);
    try { const r = await api.patch(`/submissions/${sub.submission_id}`, body); onChanged(r.data); toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  const rescore = async () => {
    setBusy(true);
    try { const r = await api.post(`/submissions/${sub.submission_id}/score`); onChanged({ ...sub, ai_score: r.data.score, ai_strengths: r.data.strengths, ai_gaps: r.data.gaps, ai_red_flags: r.data.red_flags }); toast.success("Re-scored"); }
    catch { toast.error("Scoring failed"); }
    finally { setBusy(false); }
  };
  const addNote = async () => { if (!note.trim()) return; await patch({ note: note.trim() }, "Note added"); setNote(""); };

  return (
    <div className="fixed inset-0 bg-[rgba(10,10,10,0.85)] flex justify-end z-40" onClick={onClose}>
      <div className="w-full max-w-[560px] h-full bg-[#0A0A0A] border-l border-[#262626] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="candidate-drawer">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">{sub.name || "Anonymous"}</h2>
            <div className="text-xs font-mono text-[#71717A]">{sub.email || "—"} · {sub.phone || "no phone"}</div>
            <div className="text-xs text-[#71717A] mt-0.5">{form?.title || sub.form_slug} · {sub.city || "—"} · source {sub.source || sub.utm?.source || "direct"}</div>
          </div>
          <button onClick={onClose} className="tt-btn-ghost p-1.5" data-testid="candidate-drawer-close"><X size={16} /></button>
        </div>

        <div className="flex flex-wrap items-center gap-2 my-4">
          <select value={sub.stage || ""} disabled={busy} onChange={(e) => patch({ stage: e.target.value }, `Moved to ${e.target.value}`)} className="tt-input !w-auto text-sm" data-testid={`candidate-stage-select-${sub.submission_id}`}>
            {!stages.includes(sub.stage) && sub.stage && <option value={sub.stage}>{sub.stage}</option>}
            {stages.map((s) => <option key={s} value={s}>{s === "Offer accepted" ? "Offer accepted · Hired" : s}</option>)}
          </select>
          <button onClick={() => onMessage(sub)} className="tt-btn-primary text-sm flex items-center gap-1" data-testid={`candidate-message-${sub.submission_id}`}><Send size={13} /> Message</button>
          <button onClick={rescore} disabled={busy} className="tt-btn-secondary text-sm flex items-center gap-1" data-testid={`candidate-rescore-${sub.submission_id}`}><RefreshCw size={13} /> Re-score</button>
          {typeof sub.ai_score === "number" && <span className={sub.ai_score >= 70 ? "tt-badge-pass" : "tt-badge-warn"} data-testid="candidate-ai-score">AI {Math.round(sub.ai_score)}</span>}
        </div>
        {sub.stage === "Offer accepted" && <div className="text-xs text-[#22C55E] mb-4">Hired: an employee record and pre-joining form were created automatically.</div>}

        {sub.booking?.slot_iso && (
          <div className="tt-card p-3 mb-4 text-sm" data-testid="candidate-booking">
            <div className="tt-label mb-1">Interview</div>
            <div>{fmtIST(sub.booking.slot_iso)}</div>
            {sub.booking.meet_link ? <a href={sub.booking.meet_link} target="_blank" rel="noreferrer" className="text-[#5B9BFF] underline text-xs">{sub.booking.meet_link}</a>
              : <div className="text-xs text-[#F5A524]">No calendar invite yet. Connect Google in Settings, or send the Meet link manually.</div>}
          </div>
        )}

        {sub.ai_strengths && (
          <div className="tt-card p-3 mb-5 text-sm space-y-1">
            <div><span className="text-[#22C55E] font-mono text-xs mr-2">STRENGTHS</span>{sub.ai_strengths}</div>
            <div><span className="text-[#F5A524] font-mono text-xs mr-2">GAPS</span>{sub.ai_gaps}</div>
            <div><span className="text-[#EF4444] font-mono text-xs mr-2">RED FLAGS</span>{sub.ai_red_flags}</div>
          </div>
        )}

        <div className="tt-label mb-2">Answers</div>
        <Answers form={form} answers={sub.answers} />

        <div className="tt-label mb-2">Messages</div>
        <CommsHistory log={log} />

        <div className="tt-label mb-2">Notes</div>
        <div className="space-y-2 mb-2">
          {(sub.notes || []).map((n, i) => <div key={i} className="text-sm tt-card p-2"><div>{n.text}</div><div className="text-[10px] font-mono text-[#52525B] mt-1">{n.by} · {fmtIST(n.ts)}</div></div>)}
        </div>
        <div className="flex gap-2">
          <input className="tt-input text-sm" placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(); }} data-testid={`candidate-note-${sub.submission_id}`} />
          <button onClick={addNote} disabled={busy} className="tt-btn-secondary text-sm" data-testid="candidate-note-add">Add</button>
        </div>
      </div>
    </div>
  );
}
